<template>
    <div>
        <h1 class="text-h5 my-5">
            Application categories
            <v-progress-circular v-if="loading" indeterminate color="green" class="ml-2"/>
        </h1>
        <v-alert v-if="alert.msg" :color="alert.color" icon="mdi-alert-octagon-outline" outlined text dismissible>
            {{alert.msg}}
        </v-alert>

        <p class="mt-4">
            You can organize your applications into groups by defining
            <a href="https://en.wikipedia.org/wiki/Glob_(programming)" target="_blank">glob patterns</a>
            in the <var>&lt;namespace&gt;/&lt;application_name&gt;</var> format.
        </p>

        <v-simple-table class="categories mt-4">
            <thead>
            <tr>
                <th>Category</th>
                <th>Patterns</th>
                <th>Alerting</th>
                <th>Deployment tracking</th>
                <th>Actions</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="c in categories">
                <td class="text-no-wrap">
                    <div class="text-no-wrap">{{ c.name }}</div>
                </td>
                <td>
                    <div v-if="c.default" class="grey--text">
                        The default category containing applications that don't fit into other categories
                    </div>
                    <div v-else class="py-1" style="line-height: 2em">
                        <template v-for="p in (c.builtin_patterns + ' ' + c.custom_patterns).split(' ').filter(p => !!p)">
                            <span class="pattern">{{ p }}</span>&nbsp;
                        </template>
                    </div>
                </td>
                <td>
                    <div class="text-no-wrap">
                        {{formatChannel(c.notify_of_incidents) || 'off'}}
                    </div>
                </td>
                <td>
                    <div class="text-no-wrap">
                        {{formatChannel(c.notify_of_deployments) || 'off'}}
                    </div>
                </td>
                <td>
                    <div class="d-flex">
                        <v-btn icon small @click="openForm(c)" :disabled="!canEdit"><v-icon small>mdi-pencil</v-icon></v-btn>
                        <v-btn v-if="!c.builtin" icon small @click="openForm(c, true)" :disabled="!canEdit"><v-icon small>mdi-trash-can-outline</v-icon></v-btn>
                    </div>
                </td>
            </tr>
            </tbody>
        </v-simple-table>

        <v-btn color="primary" class="mt-2" @click="openForm()" :disabled="!canEdit">Add a category</v-btn>

        <v-dialog v-model="form.active" max-width="800">
            <v-card class="pa-4">
                <div class="d-flex align-center font-weight-medium mb-4">
                    <div v-if="form.new">
                        Add a new application category
                    </div>
                    <div v-else-if="form.del">
                        Delete the "{{form.name}}" application category
                    </div>
                    <div v-else>
                        Edit the "{{form.name}}" application category
                    </div>
                    <v-spacer />
                    <v-btn icon @click="form.active = false"><v-icon>mdi-close</v-icon></v-btn>
                </div>

                <v-form v-model="form.valid" ref="form">
                    <div class="subtitle-1">Name</div>
                    <v-text-field v-model="form.new_name" outlined dense :disabled="form.builtin || form.del" :rules="[$validators.isSlug]" />

                    <template v-if="!form.default">
                        <template v-if="form.builtin">
                            <div class="subtitle-1">Built-in patterns</div>
                            <v-textarea v-model="form.builtin_patterns" outlined dense rows="1" auto-grow disabled />
                        </template>

                        <div class="subtitle-1">Custom patterns</div>
                        <div class="caption">
                            space-delimited list of
                            <a href="https://en.wikipedia.org/wiki/Glob_(programming)" target="_blank">glob patterns</a>
                            in the <var>&lt;namespace&gt;/&lt;application_name&gt;</var> format
                            , e.g.: <var>staging/* test-*/*</var>
                        </div>
                        <v-textarea v-model="form.custom_patterns" outlined dense rows="1" auto-grow :disabled="form.del"/>
                    </template>

                    <div class="subtitle-1">Notify of deployments</div>
                    <v-select :items="form.notification_channels_for_deployments" v-model="form.notify_of_deployments" dense outlined no-data-text="no available channels" menu-props="offsetY">
                        <template #selection="{item}">
                            <div v-html="item.text"></div>
                        </template>
                        <template #item="{item}">
                            <div v-html="item.text"></div>
                        </template>
                    </v-select>
                    <div class="subtitle-1">Notify of incidents</div>
                    <v-select :items="form.notification_channels_for_incidents" v-model="form.notify_of_incidents" dense outlined no-data-text="no available channels" menu-props="offsetY">
                        <template #selection="{item}">
                            <div v-html="item.text"></div>
                        </template>
                        <template #item="{item}">
                            <div v-html="item.text"></div>
                        </template>
                    </v-select>

                    <v-alert v-if="form.error" color="red" icon="mdi-alert-octagon-outline" outlined text>
                        {{form.error}}
                    </v-alert>
                    <v-alert v-if="form.message" color="green" outlined text>
                        {{form.message}}
                    </v-alert>
                    <div class="d-flex align-center">
                        <v-spacer />
                        <v-btn v-if="form.del" color="error" :loading="form.saving" @click="del">Delete</v-btn>
                        <v-btn v-else color="primary" :disabled="!form.valid" :loading="form.saving" @click="save">Save</v-btn>
                    </div>
                </v-form>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
export default {
    props: {
        workspaceId: String,
    },

    data() {
        return {
            access: '',
            loading: false,
            alert: {msg: '', color: ''},
            categories: [],
            notification_channels_for_deployments: [],
            notification_channels_for_incidents: [],
            form: {
                name: '',
                new_name: '',
                builtin_patterns: '',
                custom_patterns: '',

                builtin: false,
                default: false,
                active: false,
                new: false,
                del: false,
                valid: true,
                saving: false,
                error: '',
                message: '',

                notification_channels_for_deployments: [],
                notify_of_deployments: null,
                notification_channels_for_incidents: [],
                notify_of_incidents: null,
            },
        };
    },

    computed: {
        canEdit() {
            return this.access === 'edit';
        },
    },

    mounted() {
        this.get();
    },

    methods: {
        openForm(category, del) {
            if (!this.canEdit) {
                return;
            }
            this.error = '';

            this.form.builtin = category && category.builtin;
            this.form.default = category && category.default;
            this.form.active = true;
            this.form.new = !category;
            this.form.del = del;

            this.form.name = category ? category.name : '';
            this.form.new_name = category ? category.name : ''
            this.form.builtin_patterns = category ? category.builtin_patterns  : '';
            this.form.custom_patterns = category ? category.custom_patterns : '';

            this.$refs.form && this.$refs.form.resetValidation();

            this.form.notification_channels_for_deployments = this.formatChannels(this.notification_channels_for_deployments);
            this.form.notification_channels_for_incidents = this.formatChannels(this.notification_channels_for_incidents);
            this.form.notify_of_deployments = category && category.notify_of_deployments;
            this.form.notify_of_incidents = category && category.notify_of_incidents;
        },
        formatChannels(channels) {
            return channels.map(ch => {
                if (!ch.integration_key) {
                    return {text: 'off', value: ch};
                }
                if (ch.integration_key === 'default') {
                    const defined = this.formatChannel(ch);
                    const text = `default channel: <span class="caption grey--text ml-1">currently: ${defined || 'none'}</span>`;
                    return {text, value: ch};
                }
                return {text: this.formatChannel(ch), value: ch};
            })
        },
        formatChannel(ch) {
            if (!ch) {
                return '';
            }
            if (ch.slack) {
                return `Slack (${ch.slack.workspace} #${ch.slack.channel})`;
            }
            if (ch.teams) {
                return 'MS Teams';
            }
            if (ch.pagerduty) {
                return 'Pagerduty';
            }
            if (ch.opsgenie) {
                return 'Opsgenie';
            }
            if (ch.webhook) {
                return 'Webhook';
            }
            return '';
        },
        get() {
            this.loading = true;
            this.$api.getCategories((data, meta) => {
                this.loading = false;
                if (meta.error) {
                    this.alert.msg = 'Failed to get integrations. Please try again later.'
                    this.alert.color = 'red';
                }
                this.access = data.access;
                this.categories = data.categories || [];
                this.notification_channels_for_deployments = data.notification_channels_for_deployments || [];
                this.notification_channels_for_incidents = data.notification_channels_for_incidents || [];
                if (!this.canEdit) {
                    this.alert.msg = 'You are not allowed to configure categories.'
                    this.alert.color = 'primary';
                }
            });
        },
        save() {
            this.form.saving = true;
            this.form.error = '';
            this.form.message = '';
            const form = {
                name: this.form.name,
                new_name: this.form.new_name,
                custom_patterns: this.form.custom_patterns,
                notify_of_deployments: this.form.notify_of_deployments,
                notify_of_incidents: this.form.notify_of_incidents,
            };
            this.$api.saveCategory(form, (data, meta) => {
                this.form.saving = false;
                if (meta.error) {
                    this.form.error = meta.error;
                    return;
                }
                this.form.message = 'Settings were successfully updated.';
                setTimeout(() => {
                    this.form.message = '';
                    this.form.active = false;
                }, 1000);
                this.get();
            });
        },
        del() {
            this.form.saving = true;
            this.form.error = '';
            this.form.message = '';
            const form = {name: this.form.name, new_name: this.form.name};
            this.$api.delCategory(form, (data, meta) => {
                this.form.saving = false;
                if (meta.error) {
                    this.form.error = meta.error;
                    return;
                }
                this.form.message = 'Settings were successfully updated.';
                setTimeout(() => {
                    this.form.message = '';
                    this.form.active = false;
                }, 1000);
                this.get();
            });
        },
    },
};
</script>

<style scoped>
.pattern {
    border: 1px solid #BDBDBD;
    border-radius: 4px;
    padding: 2px 4px;
    white-space: nowrap;
}
</style>
